import type { EbookMove, EbookQuestion } from '@wx-weiqi/contracts';

// Presentation only: the server supplies the authoritative board after captures.
export function ebookMoveLabels(size: number, stones: EbookQuestion['stones'], moves: EbookMove[]) {
  const labels = new Map<
    string,
    { point: { x: number; y: number }; label: string; color: EbookMove['color'] }
  >();
  moves.forEach((move, index) => {
    if (!move.point) return; // Pass counts as a turn, but has no intersection.
    const key = `${move.point.x}:${move.point.y}`;
    labels.delete(key);
    if (stones[move.point.y * size + move.point.x] === move.color)
      labels.set(key, { point: move.point, label: String(index + 1), color: move.color });
  });
  return [...labels.values()];
}
export function branchRecord(q: EbookQuestion, id: string) {
  const path: EbookQuestion['nodes'] = [];
  let nodeId: string | null = id;
  const seen = new Set<string>();
  while (nodeId) {
    if (seen.has(nodeId)) return null;
    seen.add(nodeId);
    const node = q.nodes.find((n) => n.id === nodeId);
    if (!node) return null;
    path.unshift(node);
    nodeId = node.parentId;
  }
  return path;
}
export function savedBranchEnds(q?: EbookQuestion) {
  return (
    q?.nodes.filter(
      (node) => node.outcome !== 'CONTINUE' || !q.nodes.some((child) => child.parentId === node.id),
    ) ?? []
  );
}
export function branchIsSaved(q: EbookQuestion, saved: EbookQuestion | undefined, id: string) {
  return (
    !!saved &&
    !!branchRecord(q, id) &&
    JSON.stringify(branchRecord(q, id)) === JSON.stringify(branchRecord(saved, id))
  );
}

export function savedNextMoves(
  q: EbookQuestion,
  saved: EbookQuestion | undefined,
  nodeId: string | null,
) {
  if (
    !saved ||
    q.size !== saved.size ||
    q.currentPlayer !== saved.currentPlayer ||
    q.goal !== saved.goal ||
    JSON.stringify(q.stones) !== JSON.stringify(saved.stones)
  )
    return [];
  const prefix = nodeId ? branchRecord(q, nodeId) : [];
  if (!prefix || (prefix.length && prefix.at(-1)!.outcome !== 'CONTINUE')) return [];
  const nextMoves = new Map<
    string,
    { point: NonNullable<EbookMove['point']>; outcome: 'CORRECT' | 'INCORRECT' }
  >();
  for (const end of savedBranchEnds(saved)) {
    if (end.outcome === 'CONTINUE' || !branchIsSaved(q, saved, end.id)) continue;
    const path = branchRecord(saved, end.id)!;
    if (prefix.some((node, index) => node.id !== path[index]?.id)) continue;
    const next = path[prefix.length];
    // A pass has no board marker. Never skip it to show a later move.
    if (!next?.move.point) continue;
    const { point } = next.move;
    const key = `${point.x}:${point.y}`;
    // Common moves stay green if any saved correct continuation exists.
    const previous = nextMoves.get(key);
    if (!previous || end.outcome === 'CORRECT') nextMoves.set(key, { point, outcome: end.outcome });
  }
  return [...nextMoves.values()];
}

export function layoutAnswerTree(nodes: EbookQuestion['nodes']) {
  const children = new Map<string | null, EbookQuestion['nodes']>();
  for (const node of nodes)
    children.set(node.parentId, [...(children.get(node.parentId) ?? []), node]);
  type Item = {
    id: string | null;
    node: EbookQuestion['nodes'][number] | undefined;
    depth: number;
    x: number;
    y: number;
  };
  const items: Item[] = [],
    seen = new Set<string | null>();
  const byId = new Map<string | null, Item>();
  const stack: {
    id: string | null;
    depth: number;
    exit: boolean;
    node?: EbookQuestion['nodes'][number];
  }[] = [{ id: null, depth: 0, exit: false }];
  let leaf = 0;
  while (stack.length) {
    const entry = stack.pop()!;
    if (entry.exit) {
      const descendants = (children.get(entry.id) ?? [])
        .map((n) => byId.get(n.id))
        .filter((n): n is Item => !!n);
      // Keep the main line beside the root; additional branches grow below it.
      byId.get(entry.id)!.y = descendants.length ? descendants[0]!.y : 38 + leaf++ * 76;
      continue;
    }
    if (seen.has(entry.id)) continue;
    seen.add(entry.id);
    const item = {
      id: entry.id,
      node: entry.node,
      depth: entry.depth,
      x: 12 + entry.depth * 192,
      y: 38,
    };
    byId.set(entry.id, item);
    items.push(item);
    stack.push({ ...entry, exit: true });
    for (const node of [...(children.get(entry.id) ?? [])].reverse()) {
      if (!seen.has(node.id))
        stack.push({ id: node.id, node, depth: entry.depth + 1, exit: false });
    }
  }
  const edges = items.flatMap((item) => {
    const parent = item.node ? byId.get(item.node.parentId) : undefined;
    return parent ? [{ parent, child: item }] : [];
  });
  return {
    items,
    edges,
    width: Math.max(...items.map((item) => item.x)) + 164,
    height: Math.max(76, leaf * 76),
  };
}
